import React, { useState, useEffect } from "react";
import { COLORS } from "../constants/colors.js";
import { FONTS } from "../constants/fonts.js";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { getEquipment, deleteEquipment } from "../utils/equipmentUtils";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  DocumentArrowDownIcon,
} from "@heroicons/react/24/outline";
import { getFunctions, httpsCallable } from "firebase/functions";

const styles = {
  container: {
    flex: 1,
    backgroundColor: COLORS.white,
    minHeight: "100vh",
    position: "relative",
    maxWidth: 1400,
    margin: "0 auto",
    padding: "16px 24px",
    paddingTop: 16,
    width: "100%",
  },
  titleContainer: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    marginBottom: 24,
    animation: "fadeIn 0.5s ease-in-out",
  },
  title: {
    fontSize: 32,
    fontFamily: FONTS.bold,
    color: COLORS.text,
    fontWeight: 700,
  },
  reportButton: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 16px",
    backgroundColor: COLORS.primary,
    border: "none",
    borderRadius: 12,
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  },
  reportButtonText: {
    color: COLORS.white,
    fontSize: 14,
    fontFamily: FONTS.bold,
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 2000,
  },
  modalContent: {
    backgroundColor: COLORS.white,
    borderRadius: 12,
    padding: 24,
    width: "90%",
    maxWidth: 420,
    boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
  },
  modalTitle: {
    fontSize: 24,
    fontFamily: FONTS.bold,
    color: COLORS.text,
    marginBottom: 8,
  },
  modalText: {
    fontSize: 14,
    color: COLORS.gray,
    fontFamily: FONTS.regularInter,
    marginBottom: 16,
  },
  modalPrimaryButton: {
    width: "100%",
    padding: 12,
    backgroundColor: COLORS.primary,
    color: COLORS.white,
    border: "none",
    borderRadius: 12,
    fontSize: 16,
    fontFamily: FONTS.bold,
    cursor: "pointer",
    marginBottom: 8,
  },
  modalDivider: {
    textAlign: "center",
    color: COLORS.gray,
    fontSize: 13,
    fontFamily: FONTS.regularInter,
    margin: "12px 0",
  },
  modalInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: 12,
    borderRadius: 12,
    border: `1px solid ${COLORS.tertiary}`,
    fontSize: 16,
    fontFamily: FONTS.regularInter,
    marginBottom: 8,
  },
  modalCancelButton: {
    width: "100%",
    padding: 12,
    backgroundColor: COLORS.white,
    color: COLORS.text,
    border: `1px solid ${COLORS.tertiary}`,
    borderRadius: 12,
    fontSize: 16,
    fontFamily: FONTS.regularInter,
    cursor: "pointer",
    marginTop: 4,
  },
  filterButtons: {
    display: "flex",
    gap: 8,
    marginBottom: 16,
  },
  filterButton: {
    padding: "4px 8px",
    backgroundColor: `${COLORS.secondary}f0`,
    borderRadius: 8,
    border: `1px solid ${COLORS.tertiary}`,
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: COLORS.primary,
      borderColor: COLORS.primary,
      "& span": {
        color: COLORS.white,
      },
    },
  },
  filterButtonActive: {
    borderColor: COLORS.primary,
    backgroundColor: `${COLORS.primary}15`,
  },
  filterButtonText: {
    fontSize: 12,
    color: COLORS.primary,
    fontFamily: FONTS.regularInter,
  },
  equipmentItem: {
    display: "flex",
    alignItems: "center",
    backgroundColor: `${COLORS.secondary}f0`,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    padding: 20,
    borderRadius: 12,
    marginBottom: 16,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    border: `1px solid ${COLORS.tertiary}`,
    transition: "all 0.3s ease",
    animation: "fadeIn 0.5s ease-in-out",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: `0 6px 16px rgba(${parseInt(
        COLORS.primary.slice(1, 3),
        16
      )}, ${parseInt(COLORS.primary.slice(3, 5), 16)}, ${parseInt(
        COLORS.primary.slice(5, 7),
        16
      )}, 0.2)`,
    },
  },
  equipmentImageContainer: {
    marginRight: 16,
    flexShrink: 0,
    position: "relative",
    width: 80,
    height: 80,
  },
  equipmentImage: {
    width: 80,
    height: 80,
    borderRadius: 8,
    objectFit: "cover",
  },
  imageLoader: {
    width: 80,
    height: 80,
    borderRadius: 8,
    backgroundColor: COLORS.gray,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "absolute",
    top: 0,
    left: 0,
  },
  noImagePlaceholder: {
    width: 80,
    height: 80,
    backgroundColor: COLORS.gray,
    borderRadius: 8,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  noImageText: {
    fontSize: 12,
    color: COLORS.text,
    textAlign: "center",
    fontFamily: FONTS.regularInter,
  },
  equipmentDetails: {
    flex: 1,
  },
  equipmentText: {
    fontSize: 16,
    color: COLORS.text,
    marginBottom: 8,
    fontFamily: FONTS.regularInter,
  },
  actionButtons: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  actionButton: {
    padding: 10,
    borderRadius: 10,
    transition: "all 0.3s ease",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: COLORS.primary,
      "& svg": {
        stroke: COLORS.white,
      },
    },
  },
  deleteButton: {
    "&:hover": {
      backgroundColor: COLORS.error,
      "& svg": {
        stroke: COLORS.white,
      },
    },
  },
  addButton: {
    position: "fixed",
    bottom: 32,
    right: 32,
    border: "none",
    borderRadius: 32,
    width: 64,
    height: 64,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: COLORS.primary,
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
    cursor: "pointer",
    color: COLORS.white,
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: COLORS.secondary,
      color: COLORS.primary,
      transform: "scale(1.1)",
      boxShadow: `0 6px 20px rgba(${parseInt(
        COLORS.primary.slice(1, 3),
        16
      )}, ${parseInt(COLORS.primary.slice(3, 5), 16)}, ${parseInt(
        COLORS.primary.slice(5, 7),
        16
      )}, 0.3)`,
    },
    "&:active": {
      transform: "scale(0.95)",
    },
  },
  emptyText: {
    fontSize: 18,
    color: COLORS.gray,
    textAlign: "center",
    marginTop: 32,
    fontFamily: FONTS.regularInter,
  },
  loader: {
    fontSize: 18,
    color: COLORS.text,
    textAlign: "center",
    marginVertical: 32,
    fontFamily: FONTS.regularInter,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  errorText: {
    fontSize: 18,
    color: COLORS.error,
    textAlign: "center",
    marginTop: 32,
    fontFamily: FONTS.regularInter,
  },
  loadingSpinner: {
    border: `3px solid ${COLORS.gray}`,
    borderTop: `3px solid ${COLORS.primary}`,
    borderRadius: "50%",
    width: 24,
    height: 24,
    animation: "spin 1s linear infinite",
  },
  imageSpinner: {
    border: `2px solid ${COLORS.gray}`,
    borderTop: `2px solid ${COLORS.primary}`,
    borderRadius: "50%",
    width: 20,
    height: 20,
    animation: "spin 1s linear infinite",
  },
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  "@keyframes fadeIn": {
    "0%": { opacity: 0, transform: "translateY(10px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
  "@media (max-width: 768px)": {
    container: {
      padding: "12px 16px",
      paddingTop: 16,
    },
    title: {
      fontSize: 28,
    },
    equipmentItem: {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 12,
      padding: 16,
    },
    actionButtons: {
      width: "100%",
      justifyContent: "flex-end",
    },
    addButton: {
      bottom: 16,
      right: 16,
      width: 56,
      height: 56,
    },
    filterButtonText: {
      fontSize: 10,
    },
  },
};

const EquipmentScreen = () => {
  const { user, userType } = useAuth();
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState([]);
  const [allEquipment, setAllEquipment] = useState([]); // Store unfiltered equipment
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [imageLoading, setImageLoading] = useState({});
  const [filter, setFilter] = useState("all"); // Default to "all"
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportEmail, setReportEmail] = useState("");
  const [reportLoading, setReportLoading] = useState(false);

  const runEquipmentReport = async ({ email }) => {
    setReportLoading(true);
    try {
      const functions = getFunctions();
      const generateEquipmentReport = httpsCallable(
        functions,
        "generateEquipmentReport"
      );
      const res = await generateEquipmentReport({ email: email || "" });
      const data = res.data || {};

      if (email) {
        alert(`Equipment report emailed to ${email} (${data.total} items).`);
      } else {
        // Decode the base64 .xlsx and trigger a browser download.
        const byteChars = atob(data.base64);
        const bytes = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
          bytes[i] = byteChars.charCodeAt(i);
        }
        const blob = new Blob([bytes], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = data.filename || "equipment-report.xlsx";
        link.click();
        URL.revokeObjectURL(url);
      }
      setShowReportModal(false);
      setReportEmail("");
    } catch (error) {
      console.error("Equipment report error:", error);
      alert("Failed to generate report: " + error.message);
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    if (!user || userType !== "admin") return;
    const fetchEquipment = async () => {
      try {
        setLoading(true);
        const equipmentList = await getEquipment();
        setAllEquipment(equipmentList || []);
        // Apply filter to equipment list
        const filteredEquipment = equipmentList.filter((item) => {
          if (filter === "all") return true;
          if (filter === "available") return item.status === "available";
          if (filter === "rented") return item.status === "rented out";
          return true;
        });
        setEquipment(filteredEquipment || []);
        setError(null);
        setImageLoading(
          equipmentList.reduce(
            (acc, item) => ({
              ...acc,
              [item.id]: item.images && item.images.length > 0,
            }),
            {}
          )
        );
      } catch (error) {
        console.error("Fetch equipment error:", error);
        setError("Failed to load equipment. Please try again.");
        setEquipment([]);
        setAllEquipment([]);
      } finally {
        setLoading(false);
      }
    };
    fetchEquipment();
  }, [user, userType, filter]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this equipment?")) {
      return;
    }
    setDeletingId(id);
    try {
      await deleteEquipment(id);
      setAllEquipment((prev) => prev.filter((item) => item.id !== id));
      setEquipment((prev) => prev.filter((item) => item.id !== id));
      alert("Equipment deleted successfully");
    } catch (error) {
      alert("Failed to delete equipment");
    } finally {
      setDeletingId(null);
    }
  };

  const handleImageLoad = (id) => {
    setImageLoading((prev) => ({ ...prev, [id]: false }));
  };

  const handleImageError = (id) => {
    setImageLoading((prev) => ({ ...prev, [id]: false }));
  };

  const renderEquipmentItem = (item) => {
    return (
      <div style={styles.equipmentItem} key={item.id}>
        <div style={styles.equipmentImageContainer}>
          {item.images && item.images.length > 0 ? (
            <>
              {imageLoading[item.id] && (
                <div style={styles.imageLoader}>
                  <div style={styles.imageSpinner}></div>
                </div>
              )}
              <img
                src={item.images[0]}
                alt="Equipment"
                style={{
                  ...styles.equipmentImage,
                  display: imageLoading[item.id] ? "none" : "block",
                }}
                onLoad={() => handleImageLoad(item.id)}
                onError={() => handleImageError(item.id)}
              />
            </>
          ) : (
            <div style={styles.noImagePlaceholder}>
              <span style={styles.noImageText}>No Image</span>
            </div>
          )}
        </div>
        <div style={styles.equipmentDetails}>
          <span style={styles.equipmentText}>{item.name}</span>
          <span style={styles.equipmentText}>{item.description || "N/A"}</span>
        </div>
        <div style={styles.actionButtons}>
          <button
            style={{ ...styles.actionButton }}
            onClick={() =>
              navigate("/admin/equipment/add", { state: { equipment: item } })
            }
          >
            <PencilIcon width={24} height={24} stroke={COLORS.primary} />
          </button>
          <button
            style={{ ...styles.actionButton, ...styles.deleteButton }}
            onClick={() => handleDelete(item.id)}
            disabled={deletingId === item.id}
          >
            {deletingId === item.id ? (
              <div style={styles.loadingSpinner}></div>
            ) : (
              <TrashIcon width={24} height={24} stroke={COLORS.error} />
            )}
          </button>
        </div>
      </div>
    );
  };

  if (!user || userType !== "admin") {
    return (
      <div style={styles.loader}>
        <div style={styles.loadingSpinner}></div>
        <span style={styles.emptyText}>Unauthorized access</span>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={{ ...styles.titleContainer, justifyContent: "space-between" }}>
        <h1 style={styles.title}>Equipment</h1>
        <button
          style={styles.reportButton}
          onClick={() => setShowReportModal(true)}
        >
          <DocumentArrowDownIcon width={20} height={20} stroke={COLORS.white} />
          <span style={styles.reportButtonText}>Report</span>
        </button>
      </div>
      <div style={styles.filterButtons}>
        <button
          style={{
            ...styles.filterButton,
            ...(filter === "all" ? styles.filterButtonActive : {}),
          }}
          onClick={() => setFilter("all")}
        >
          <span style={styles.filterButtonText}>All</span>
        </button>
        <button
          style={{
            ...styles.filterButton,
            ...(filter === "available" ? styles.filterButtonActive : {}),
          }}
          onClick={() => setFilter("available")}
        >
          <span style={styles.filterButtonText}>Available</span>
        </button>
        <button
          style={{
            ...styles.filterButton,
            ...(filter === "rented" ? styles.filterButtonActive : {}),
          }}
          onClick={() => setFilter("rented")}
        >
          <span style={styles.filterButtonText}>Rented</span>
        </button>
      </div>
      {loading ? (
        <div style={styles.loader}>
          <div style={styles.loadingSpinner}></div>
          <span style={styles.emptyText}>Loading equipment...</span>
        </div>
      ) : error ? (
        <div style={styles.loader}>
          <div style={styles.loadingSpinner}></div>
          <span style={styles.errorText}>{error}</span>
        </div>
      ) : equipment.length === 0 ? (
        <div style={styles.loader}>
          <span style={styles.emptyText}>No equipment found</span>
        </div>
      ) : (
        <div>{equipment.map(renderEquipmentItem)}</div>
      )}
      <button
        style={styles.addButton}
        onClick={() => navigate("/admin/equipment/add")}
      >
        <PlusIcon width={30} height={30} stroke={COLORS.white} />
      </button>
      {showReportModal && (
        <div
          style={styles.modalOverlay}
          onClick={() => !reportLoading && setShowReportModal(false)}
        >
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Equipment Report</h2>
            <p style={styles.modalText}>
              A spreadsheet of all equipment, costs, and current rental status.
            </p>
            <button
              style={styles.modalPrimaryButton}
              onClick={() => runEquipmentReport({ email: "" })}
              disabled={reportLoading}
            >
              {reportLoading ? "Working..." : "Download Excel"}
            </button>
            <div style={styles.modalDivider}>or email it</div>
            <input
              type="email"
              value={reportEmail}
              onChange={(e) => setReportEmail(e.target.value)}
              placeholder="recipient@example.com"
              style={styles.modalInput}
              disabled={reportLoading}
            />
            <button
              style={{
                ...styles.modalPrimaryButton,
                opacity: reportEmail.trim() && !reportLoading ? 1 : 0.5,
              }}
              onClick={() => runEquipmentReport({ email: reportEmail.trim() })}
              disabled={!reportEmail.trim() || reportLoading}
            >
              {reportLoading ? "Working..." : "Email Report"}
            </button>
            <button
              style={styles.modalCancelButton}
              onClick={() => setShowReportModal(false)}
              disabled={reportLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EquipmentScreen;
